import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ModalService } from '../../services/modal.service';
import { ServiceMedia, ServiceProviderService } from '../../services/service-provider.service';

// Reusable View/Upload buttons for media scoped to a service PROVIDER.
// Mirrors ItemMediaComponent so provider media can surface outside the
// provider form — e.g. read-only next to the business card in the
// arrangement transfer panel.
//
// Render modes:
//   default   labelled buttons (Documents / Images) — provider catalogue form
//   viewOnly  hides the Upload action — arrangement panels
//   iconOnly  compact icon-only buttons with tooltips, disabled when there is
//             no media to view. Counts are loaded eagerly so the disabled state
//             is correct on first paint. Implies viewOnly.
//
//   <app-provider-media [serviceId]="123" [iconOnly]="true"></app-provider-media>
@Component({
  selector: 'app-provider-media',
  templateUrl: './provider-media.component.html',
})
export class ProviderMediaComponent implements OnChanges {
  @Input() serviceId!: number | string | null | undefined;
  @Input() compact = false; // smaller buttons for dense layouts
  @Input() viewOnly = false; // hide the Upload action (e.g. arrangement panel)
  @Input() iconOnly = false; // compact icon buttons with tooltip + disabled-when-empty

  // Eagerly loaded in iconOnly mode so the disabled state is correct before the
  // user hovers — and so a fresh modal fetch on click can compare against this.
  docCount = 0;
  imageCount = 0;

  constructor(
    private modalService: ModalService,
    private serviceProviderService: ServiceProviderService,
    private messageService: MessageService
  ) {}

  get ready(): boolean {
    return this.serviceId != null;
  }

  private get providerId(): number {
    return Number(this.serviceId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Counts only matter in iconOnly mode (drives disabled state).
    if (!this.iconOnly) return;
    if (!('serviceId' in changes)) return;
    this.docCount = 0;
    this.imageCount = 0;
    if (!this.ready) return;
    // Single un-kinded fetch; partition client-side. Silent on error — this is
    // a passive affordance, not a primary action.
    this.serviceProviderService.getProviderMedia(this.providerId).subscribe({
      next: (items: ServiceMedia[]) => {
        this.docCount = items.filter((i) => i.kind === 'document').length;
        this.imageCount = items.filter((i) => i.kind === 'image').length;
      },
      error: () => undefined,
    });
  }

  openView(kind: 'document' | 'image'): void {
    if (!this.ready) return;
    this.serviceProviderService.getProviderMedia(this.providerId, kind).subscribe({
      next: (items) =>
        this.modalService.mediaViewer({
          kind,
          items,
          title: kind === 'image' ? 'Provider images' : 'Provider documents',
          emptyMessage:
            kind === 'image' ? 'No images uploaded yet.' : 'No documents uploaded yet.',
        }),
      error: () =>
        this.messageService.add({
          severity: 'error',
          summary: 'Could not load media',
          detail: 'Please try again.',
        }),
    });
  }

  // Arrow-fn wrappers for PrimeNG MenuItem.command (preserves `this`).
  uploadDocuments = (): void => this.openUpload('document');
  uploadImages = (): void => this.openUpload('image');

  openUpload(kind: 'document' | 'image'): void {
    if (!this.ready) return;
    const ref = this.modalService.fileUpload({
      title: kind === 'image' ? 'Upload images' : 'Upload documents',
      bucketPrefix: kind === 'image' ? 'service-provider-images' : 'service-provider-documents',
      accept: kind === 'image' ? 'image/*' : '*',
      multiple: true,
    });
    ref.onClose.subscribe((result?: { files: { id: number }[] }) => {
      if (!result?.files?.length) return;
      const fileIds = result.files.map((f) => f.id);
      this.serviceProviderService.addProviderMedia(this.providerId, fileIds, kind).subscribe({
        next: () => {
          // Keep the in-memory count fresh so the disabled state flips immediately.
          if (kind === 'image') this.imageCount += fileIds.length;
          else this.docCount += fileIds.length;
          this.messageService.add({
            severity: 'success',
            summary: 'Saved',
            detail: `${fileIds.length} ${kind === 'image' ? 'image' : 'document'}(s) added.`,
          });
        },
        error: () =>
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: 'The files uploaded but could not be linked. Please try again.',
          }),
      });
    });
  }
}
